import React, { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForgotPasswordMutation } from "../api/authApi";

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [forgotPassword, { isLoading, error }] = useForgotPasswordMutation();

  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLocalError(null);
    setSuccess(null);

    try {
      await forgotPassword({ email }).unwrap();
      setSuccess(
        `Password reset instructions have been sent to ${email}. Please check your inbox and follow the link to reset your password.`
      );
    } catch (err: any) {
      setLocalError(err?.data?.message || err?.message || "Request failed.");
    }
  };

  const getErrorMessage = () => {
    if (localError) return localError;
    if (!error) return null;
    if ("status" in error) {
      if (typeof error.status === "number")
        return `Server Error: ${error.status}`;
      if (error.status === "FETCH_ERROR")
        return "Network error: Cannot reach the server.";
      return "A connection error occurred.";
    }
    return (
      (error as any)?.data?.message ||
      (error as any)?.message ||
      "Request failed"
    );
  };

  const errorMessage = getErrorMessage();

  return (
    <div className="fixed inset-0 h-screen w-screen flex overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-900">
      <div className="flex-1 flex items-center justify-center px-4 py-10 lg:px-8">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-slate-100 px-6 py-8 sm:px-8 sm:py-10">
            <div className="mb-6 text-center">
              <h2 className="text-xl font-semibold text-slate-900">
                Forgot Password
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Enter your registered email address and we'll send you a link to reset your password.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Email
                </label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-indigo-500"
                  placeholder="you@example.com"
                />
              </div>

              {errorMessage && (
                <div className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                  {errorMessage}
                </div>
              )}

              {success && (
                <div className="text-xs font-semibold text-green-700 bg-green-50 border border-green-100 rounded-md px-3 py-2">
                  {success}
                </div>
              )}

              <button
                disabled={isLoading}
                type="submit"
                className="w-full py-2.5 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {isLoading ? "Sending..." : "Send Reset"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/login")}
                className="w-full py-2.5 text-sm font-medium bg-slate-100 text-slate-800 rounded-md hover:bg-slate-200 transition-colors"
              >
                Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
